import React from "react";

/* Required indicator with screen-reader label */
const RequiredMark = () => (
    <span
        aria-label="required"
        className="ml-1"
        style={{ color: "var(--accent-magenta)" }}
    >
        *
    </span>
);

/* Shared visual styling for inputs / textareas / selects.
   Uses `focus-visible:` so the keyboard focus ring only appears on tab
   (not on mouse click), matching Phase 1's accent-yellow focus pattern.
   WCAG 2.4.7 — Focus Visible. */
const fieldClass =
    "vfs-form-control w-full rounded-xl px-4 py-3 text-base bg-white/5 border transition-colors focus:outline-none focus-visible:outline-none";

const fieldStyle = (hasError) => ({
    minHeight: 48,
    fontSize: 16,
    color: "var(--text-primary)",
    borderColor: hasError ? "#EF4444" : "rgba(255,255,255,0.14)",
    backgroundColor: "rgba(255,255,255,0.04)",
});

/* ---------- FormField (text / email / tel) ---------- */
export const FormField = ({
    id,
    name,
    label,
    type = "text",
    value,
    onChange,
    onBlur,
    required = false,
    error,
    placeholder,
    autoComplete,
    maxLength,
    testId,
    inputMode,
}) => {
    const errId = `${id}-error`;
    return (
        <div className="flex flex-col gap-2">
            <label
                htmlFor={id}
                className="text-sm font-medium"
                style={{ color: "var(--text-primary)" }}
            >
                {label}
                {required && <RequiredMark />}
            </label>
            <input
                id={id}
                name={name}
                type={type}
                value={value || ""}
                onChange={(e) => onChange(name, e.target.value)}
                onBlur={onBlur ? () => onBlur(name) : undefined}
                placeholder={placeholder}
                autoComplete={autoComplete}
                maxLength={maxLength}
                inputMode={inputMode}
                aria-required={required || undefined}
                aria-invalid={!!error || undefined}
                aria-describedby={error ? errId : undefined}
                data-testid={testId}
                className={fieldClass}
                style={fieldStyle(!!error)}
            />
            {error && (
                <p
                    id={errId}
                    role="alert"
                    className="text-sm"
                    style={{ color: "#FCA5A5" }}
                    data-testid={`${testId}-error`}
                >
                    {error}
                </p>
            )}
        </div>
    );
};

/* ---------- FormSelect (native, styled) ---------- */
export const FormSelect = ({
    id,
    name,
    label,
    value,
    onChange,
    onBlur,
    required = false,
    error,
    options,
    placeholder = "Select an option",
    testId,
}) => {
    const errId = `${id}-error`;
    return (
        <div className="flex flex-col gap-2">
            <label
                htmlFor={id}
                className="text-sm font-medium"
                style={{ color: "var(--text-primary)" }}
            >
                {label}
                {required && <RequiredMark />}
            </label>
            <select
                id={id}
                name={name}
                value={value || ""}
                onChange={(e) => onChange(name, e.target.value)}
                onBlur={onBlur ? () => onBlur(name) : undefined}
                aria-required={required || undefined}
                aria-invalid={!!error || undefined}
                aria-describedby={error ? errId : undefined}
                data-testid={testId}
                className={fieldClass + " appearance-none cursor-pointer"}
                style={{
                    ...fieldStyle(!!error),
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' fill='none' stroke='%23C4B5FD' stroke-width='1.6'/></svg>\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 1rem center",
                    paddingRight: "2.5rem",
                }}
            >
                <option value="" disabled>
                    {placeholder}
                </option>
                {options.map((opt) => (
                    <option
                        key={opt}
                        value={opt}
                        style={{ background: "#1A0B3D", color: "#F5F3FF" }}
                    >
                        {opt}
                    </option>
                ))}
            </select>
            {error && (
                <p
                    id={errId}
                    role="alert"
                    className="text-sm"
                    style={{ color: "#FCA5A5" }}
                    data-testid={`${testId}-error`}
                >
                    {error}
                </p>
            )}
        </div>
    );
};

/* ---------- FormTextarea ---------- */
export const FormTextarea = ({
    id,
    name,
    label,
    value,
    onChange,
    onBlur,
    required = false,
    error,
    maxLength = 2000,
    minRows = 4,
    placeholder,
    testId,
}) => {
    const errId = `${id}-error`;
    const len = (value || "").length;
    return (
        <div className="flex flex-col gap-2">
            <label
                htmlFor={id}
                className="text-sm font-medium"
                style={{ color: "var(--text-primary)" }}
            >
                {label}
                {required && <RequiredMark />}
            </label>
            <textarea
                id={id}
                name={name}
                rows={minRows}
                value={value || ""}
                onChange={(e) => onChange(name, e.target.value)}
                onBlur={onBlur ? () => onBlur(name) : undefined}
                placeholder={placeholder}
                maxLength={maxLength}
                aria-required={required || undefined}
                aria-invalid={!!error || undefined}
                aria-describedby={error ? errId : undefined}
                data-testid={testId}
                className={fieldClass + " resize-y"}
                style={{ ...fieldStyle(!!error), minHeight: 120 }}
            />
            <div className="flex justify-between text-xs">
                <span
                    id={errId}
                    role="alert"
                    style={{ color: error ? "#FCA5A5" : "transparent" }}
                    data-testid={error ? `${testId}-error` : undefined}
                >
                    {error || "."}
                </span>
                <span style={{ color: "var(--text-muted)" }}>
                    {len}/{maxLength}
                </span>
            </div>
        </div>
    );
};

/* ---------- FormCheckboxGroup ---------- */
export const FormCheckboxGroup = ({
    name,
    legend,
    value = [],
    onChange,
    options,
    required = false,
    error,
    testId,
}) => {
    const toggle = (opt) => {
        const next = value.includes(opt)
            ? value.filter((v) => v !== opt)
            : [...value, opt];
        onChange(name, next);
    };
    const errId = `${name}-error`;
    return (
        <fieldset
            className="flex flex-col gap-3"
            aria-invalid={!!error || undefined}
            aria-describedby={error ? errId : undefined}
            data-testid={testId}
        >
            <legend
                className="text-sm font-medium mb-1"
                style={{ color: "var(--text-primary)" }}
            >
                {legend}
                {required && <RequiredMark />}
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {options.map((opt) => {
                    const checked = value.includes(opt);
                    return (
                        <label
                            key={opt}
                            className="vfs-checkbox-label flex items-start gap-3 px-4 py-3 rounded-xl cursor-pointer transition-colors"
                            style={{
                                background: checked
                                    ? "rgba(139, 92, 246, 0.12)"
                                    : "rgba(255,255,255,0.03)",
                                border: checked
                                    ? "1px solid rgba(139, 92, 246, 0.55)"
                                    : "1px solid rgba(255,255,255,0.10)",
                                minHeight: 48,
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggle(opt)}
                                aria-label={opt}
                                data-testid={`${testId}-${opt
                                    .toLowerCase()
                                    .replace(/[^a-z0-9]+/g, "-")
                                    .replace(/^-|-$/g, "")}`}
                                className="vfs-form-control-checkbox mt-0.5 w-5 h-5 cursor-pointer accent-fuchsia-500"
                            />
                            <span
                                className="text-sm font-medium"
                                style={{ color: "var(--text-primary)" }}
                            >
                                {opt}
                            </span>
                        </label>
                    );
                })}
            </div>
            {error && (
                <p
                    id={errId}
                    role="alert"
                    className="text-sm"
                    style={{ color: "#FCA5A5" }}
                    data-testid={`${testId}-error`}
                >
                    {error}
                </p>
            )}
        </fieldset>
    );
};

/* ---------- FormConsentCheckbox (single required consent) ---------- */
export const FormConsentCheckbox = ({
    id,
    name,
    label,
    checked,
    onChange,
    error,
    testId,
}) => {
    const errId = `${id}-error`;
    return (
        <div className="flex flex-col gap-2">
            <label
                htmlFor={id}
                className="vfs-checkbox-label flex items-start gap-3 px-4 py-3 rounded-xl cursor-pointer"
                style={{
                    background: checked
                        ? "rgba(20, 184, 166, 0.10)"
                        : "rgba(255,255,255,0.03)",
                    border: error
                        ? "1px solid #EF4444"
                        : checked
                            ? "1px solid rgba(20, 184, 166, 0.45)"
                            : "1px solid rgba(255,255,255,0.10)",
                    minHeight: 48,
                }}
            >
                <input
                    id={id}
                    name={name}
                    type="checkbox"
                    checked={!!checked}
                    onChange={(e) => onChange(name, e.target.checked)}
                    aria-required="true"
                    aria-invalid={!!error || undefined}
                    aria-describedby={error ? errId : undefined}
                    data-testid={testId}
                    className="vfs-form-control-checkbox mt-0.5 w-5 h-5 cursor-pointer accent-teal-500"
                />
                <span
                    className="text-sm leading-relaxed"
                    style={{ color: "var(--text-primary)" }}
                >
                    {label}
                </span>
            </label>
            {error && (
                <p
                    id={errId}
                    role="alert"
                    className="text-sm"
                    style={{ color: "#FCA5A5" }}
                    data-testid={`${testId}-error`}
                >
                    {error}
                </p>
            )}
        </div>
    );
};

/* ---------- HoneypotField ---------- */
export const HoneypotField = ({ value, onChange }) => (
    <div
        aria-hidden="true"
        style={{
            position: "absolute",
            left: -9999,
            top: "auto",
            width: 0,
            height: 0,
            overflow: "hidden",
            opacity: 0,
        }}
    >
        <label htmlFor="hp_company_website">
            Company website (leave blank)
        </label>
        <input
            id="hp_company_website"
            name="hp_company_website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={value || ""}
            onChange={(e) => onChange("hp_company_website", e.target.value)}
            data-testid="hp-company-website"
        />
    </div>
);

/* ---------- FormSubmitButton ---------- */
export const FormSubmitButton = ({
    children,
    submitting,
    testId,
    disabled,
}) => (
    <button
        type="submit"
        disabled={submitting || disabled}
        className="vfs-cta"
        style={{
            opacity: submitting || disabled ? 0.7 : 1,
            cursor: submitting || disabled ? "not-allowed" : "pointer",
        }}
        data-testid={testId}
    >
        {submitting ? (
            <>
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-label="Submitting"
                    style={{ animation: "vfs-spin 0.8s linear infinite" }}
                >
                    <circle
                        cx="12"
                        cy="12"
                        r="9"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        opacity="0.25"
                    />
                    <path
                        d="M21 12a9 9 0 0 0-9-9"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                    />
                </svg>
                <span>Sending…</span>
            </>
        ) : (
            children
        )}
        <style>{`
          @keyframes vfs-spin { to { transform: rotate(360deg) } }
        `}</style>
    </button>
);

/* ---------- FormErrorBanner ---------- */
export const FormErrorBanner = ({ message, testId = "form-error-banner" }) => {
    if (!message) return null;
    return (
        <div
            role="alert"
            data-testid={testId}
            className="rounded-xl px-5 py-4 mb-5"
            style={{
                background: "rgba(239, 68, 68, 0.08)",
                border: "1px solid rgba(239, 68, 68, 0.45)",
                color: "var(--text-primary)",
            }}
        >
            <p className="text-sm">{message}</p>
        </div>
    );
};

/* ---------- FormSuccessState ---------- */
export const FormSuccessState = ({
    heading,
    body,
    onReset,
    testId = "form-success",
}) => {
    const headingRef = React.useRef(null);
    React.useEffect(() => {
        headingRef.current?.focus();
    }, []);
    return (
        <div
            className="vfs-glass p-8 sm:p-12 text-center"
            style={{ borderRadius: 20 }}
            data-testid={testId}
        >
            <div
                className="mx-auto mb-6 inline-flex items-center justify-center w-16 h-16 rounded-2xl"
                style={{
                    background:
                        "linear-gradient(135deg, rgba(236, 72, 153, 0.18), rgba(59, 130, 246, 0.18))",
                    border: "1px solid rgba(255,255,255,0.16)",
                }}
                aria-hidden="true"
            >
                <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
                    <defs>
                        <linearGradient
                            id="vfs-check-grad"
                            x1="0"
                            y1="0"
                            x2="34"
                            y2="34"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop offset="0%" stopColor="#EC4899" />
                            <stop offset="100%" stopColor="#3B82F6" />
                        </linearGradient>
                    </defs>
                    <path
                        d="M8 17.5 L14.5 24 L26 12"
                        stroke="url(#vfs-check-grad)"
                        strokeWidth="3.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </div>
            <h2
                ref={headingRef}
                tabIndex={-1}
                className="vfs-h2 mb-4 outline-none"
                data-testid={`${testId}-heading`}
            >
                {heading}
            </h2>
            <p
                className="text-base sm:text-lg max-w-xl mx-auto leading-relaxed mb-8"
                style={{ color: "var(--text-secondary)" }}
            >
                {body}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                    type="button"
                    onClick={onReset}
                    className="vfs-cta-outline"
                    data-testid={`${testId}-reset`}
                >
                    Submit another
                </button>
                <a
                    href="/"
                    className="vfs-cta-outline"
                    data-testid={`${testId}-home`}
                >
                    Back to Home
                </a>
            </div>
        </div>
    );
};
